import React, { useEffect, useState } from 'react';
import ChevronBackOutlineIcon from '@assets/icons/chevron-back-outline-gray.svg';
import ChevronDownIcon from '@assets/icons/chevron-down.svg';
import ChevronNextIcon from '@assets/icons/chevron-forward-outline.svg';
import ChevronNextOutlineIcon from '@assets/icons/chevron-forward-outline-gray.svg';
import ChevronUpIcon from '@assets/icons/chevron-up.svg';
import TooltipIcon from '@assets/icons/tooltip-icon.svg';
import { BaseButton, BaseCarousel, BaseTooltip, EmptyResult } from '@components';
import { GRID_ROW_MARGIN_BOTTOM } from '@components/carousel/styled.ts';
import { LEARNING_TAB_BAR_SECTION_ID } from '@constants/anchors.ts';
import { LEARNING_STATES } from '@constants/learningStates.ts';
import { BodySmSmMedium } from '@constants/text.ts';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { TooltipContent } from '@routes/___shared___/styled.tsx';
import type { LearningPathType, LearningResponse } from '@routes/sidebar/learning/api/learning.api.ts';
import {
    deleteLearningCardFromList,
    getBookmarks,
    setLearningBookmarkState,
    toggleBookmark,
    updateLearningCardsRequestData,
} from '@routes/sidebar/learning/model/learning.slice.ts';
import {
    Arrows,
    Frame,
    FrameHeader,
    FrameHeaderText,
    LearningCarouselHeader,
    LearningCarouselItems,
    LearningCarousels,
    LockedContentNoItemsWrapper,
    LockIconWrapper,
    NextBtnIconWrapper,
    RotateSvg,
} from '@routes/sidebar/learning/styled.tsx';
import LearningCard from '@routes/sidebar/learning/ui/learning-tab/learning/LearningCard.tsx';
import {
    ArrowsWrapper,
    PathwaysAvailable,
    ShowMore,
    ShowMoreTextWrapper,
} from '@routes/sidebar/learning/ui/learning-tab/learning/styled.tsx';
import { LockedContent } from '@routes/sidebar/learning/ui/learning-tab/locked-content';
import type { EmblaCarouselType } from 'embla-carousel';
import { chunk } from 'lodash';

interface Props {
    isLocked: boolean;
    heading: string;
    subheader: string;
    tooltipText: string;
    tabKey: string;
    learningPathType: LearningPathType;
    pathwaysAvailable?: number; // TODO: from endpoint when ready
    isMandatorySection?: boolean;
}

const renderArrows = (prev: () => void, next: () => void, hasPrev: boolean, hasNext: boolean) => (
    // prevent click events from bubbling up to the parent because of unwanted side effects
    <Arrows onClick={(e) => e.stopPropagation()}>
        {hasPrev ? (
            <BaseButton
                $customType={'outline'}
                onClick={prev}
                icon={
                    <RotateSvg>
                        <ChevronNextIcon />
                    </RotateSvg>
                }
            />
        ) : (
            <ChevronBackOutlineIcon />
        )}
        {hasNext ? (
            <BaseButton
                $customType={'outline'}
                onClick={next}
                icon={
                    <NextBtnIconWrapper>
                        <ChevronNextIcon />
                    </NextBtnIconWrapper>
                }
            />
        ) : (
            <ChevronNextOutlineIcon />
        )}
    </Arrows>
);

const CARD_HEIGHT_PX = 521;
const SHOW_MORE_ELEMENTS_SIZE = 9;
const SHOW_LESS_ELEMENTS_SIZE = 3;
const SHOW_MORE_ROWS = 3;
const SLIDE_WIDTH = '100%'; // tells embla that the slide should take all available space
// min width to show 3 cards even if last one will be cut but start btn still clickable
const THREE_CLICKABLE_CARDS_MIN_WIDTH = 1328;

const calculateGridHeight = (
    items: any[],
    slideIndex: number,
    itemsPerSlide: number,
    rowGap: number,
    screenWidth: number,
): string => {
    if (items.length <= 0) return 'auto';

    const slides = chunk(items, itemsPerSlide);
    const currentCount = slides[slideIndex]?.length ?? 0;

    const columnsPerRow = getColumnsPerRow(screenWidth);
    const rows = Math.max(1, Math.ceil(currentCount / columnsPerRow));

    return `${rows * CARD_HEIGHT_PX + (rows - 1) * rowGap}px`;
};

const getColumnsPerRow = (width: number): number => {
    if (width >= THREE_CLICKABLE_CARDS_MIN_WIDTH) return 3;

    return 2;
};

const MyLearningTab: React.FC<Props> = ({ isLocked, heading, subheader, tooltipText, learningPathType, tabKey }) => {
    const { t } = useLingui();
    const learningCards = useAppSelector((state) => state.learning.learningCards)[learningPathType];
    const bookmarks = useAppSelector((state) => state.learning.bookmarks);

    const [index, setIndex] = useState(0);
    const [isShowingMore, setIsShowingMore] = useState(false);
    const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
    const [pageHeight, setPageHeight] = useState<string>('auto');
    const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
    const [shouldScrollNextAfterFetch, setShouldScrollNextAfterFetch] = useState(false);

    const columnsPerRow = getColumnsPerRow(screenWidth);
    const slidesPerView = 1;
    const gridItemsSize = isShowingMore ? columnsPerRow * SHOW_MORE_ROWS : columnsPerRow;

    const dispatch = useAppDispatch();
    const requestData = useAppSelector((state) => state.learning.learningCardsRequestData)[learningPathType];
    const learningCardItems = learningCards.data;

    const totalLearningCards = learningCards.pagination.totalCount;
    const carouselSlidesCount = Math.ceil(learningCardItems.length / gridItemsSize);
    const hasPrevCarouselSlide = index > 0;
    const hasNextCarouselSlide = index < carouselSlidesCount - 1;
    const hasPrev = (emblaApi?.canScrollPrev() ?? false) || hasPrevCarouselSlide;
    const isLearningDataAvailable = totalLearningCards > 0;
    const hasMoreServerPages = learningCards.pagination.pageNumber < learningCards.pagination.totalPages;
    const hasNext = (emblaApi?.canScrollNext() ?? false) || hasNextCarouselSlide || hasMoreServerPages;
    const hasPagination = isLearningDataAvailable && (hasNext || hasPrev);
    const hasEnoughItemsForControls = totalLearningCards > columnsPerRow;
    const showCarouselControls = hasEnoughItemsForControls && (hasPagination || isShowingMore) && !isLocked;

    const onPaginationChange = (pageNumber: number, pageSize = 6) => {
        dispatch(
            updateLearningCardsRequestData({
                learningPathType,
                requestData: {
                    ...requestData,
                    pageNumber,
                    pageSize,
                },
            }),
        );
    };

    const prev = () => {
        if (!emblaApi || (!emblaApi.canScrollPrev() && !hasPrevCarouselSlide)) return;
        emblaApi.scrollPrev();
    };

    const next = () => {
        if (emblaApi && (emblaApi.canScrollNext() || hasNextCarouselSlide)) {
            emblaApi.reInit();
            emblaApi.scrollNext();
            return;
        }

        if (hasMoreServerPages && !shouldScrollNextAfterFetch) {
            setShouldScrollNextAfterFetch(true);
            const pageNumber = requestData?.pageNumber ?? 1;
            const pageSize = requestData.pageSize || 6;
            onPaginationChange(pageNumber + 1, pageSize);
        }
    };

    useEffect(() => {
        if (!shouldScrollNextAfterFetch || !emblaApi) return;

        const frame = requestAnimationFrame(() => {
            emblaApi.reInit();

            if (emblaApi.canScrollNext()) {
                emblaApi.scrollNext();
                setShouldScrollNextAfterFetch(false);
            }
        });

        return () => cancelAnimationFrame(frame);
    }, [learningCards.data.length, emblaApi, shouldScrollNextAfterFetch]);

    useEffect(() => {
        if (!emblaApi) return;

        const frame = requestAnimationFrame(() => {
            emblaApi.reInit();
        });

        return () => cancelAnimationFrame(frame);
    }, [tabKey, learningCardItems.length, gridItemsSize, emblaApi]);

    const toggleShowMore = () => {
        // We always want double page size for carousel
        const pageSize = (!isShowingMore ? SHOW_MORE_ELEMENTS_SIZE : SHOW_LESS_ELEMENTS_SIZE) * 2;
        // Always reset real Embla position when changing grid size.
        // Without this, React index can be 0 while Embla is still on the last slide.
        emblaApi?.scrollTo(0, true);
        setIndex(0);
        setIsShowingMore(!isShowingMore);
        onPaginationChange(1, pageSize);
    };

    const onBookmarkClick = (learning: LearningResponse, e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();

        const isCurrentlyBookmarked =
            bookmarks.includes(learning.id) ||
            Boolean(learning.userActivity?.states.some((state) => state === LEARNING_STATES.Bookmarked));

        const nextIsBookmarked = !isCurrentlyBookmarked;
        const shouldRemoveFromBookmarkedTab = tabKey === LEARNING_STATES.Bookmarked && !nextIsBookmarked;

        dispatch(setLearningBookmarkState({ id: learning.id, isBookmarked: nextIsBookmarked }));

        dispatch(toggleBookmark(learning.id))
            .then(() => {
                void dispatch(getBookmarks());

                if (shouldRemoveFromBookmarkedTab) {
                    dispatch(deleteLearningCardFromList({ learningPathType, id: learning.id }));
                }
            })
            .catch(() => {
                dispatch(setLearningBookmarkState({ id: learning.id, isBookmarked: isCurrentlyBookmarked }));
            });
    };

    useEffect(() => {
        const recalcHeight = () => {
            const height = calculateGridHeight(
                learningCardItems,
                index,
                gridItemsSize,
                GRID_ROW_MARGIN_BOTTOM,
                screenWidth,
            );
            setPageHeight(height);
        };

        recalcHeight();
    }, [learningCardItems, index, isShowingMore, screenWidth, gridItemsSize]);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <LearningCarousels data-cy="learning-section" data-learning-path-type={learningPathType}>
                <LearningCarouselHeader>
                    <Frame>
                        <FrameHeader>
                            {!!(isLearningDataAvailable && isLocked) && <LockIconWrapper />}
                            <FrameHeaderText>{heading}</FrameHeaderText>
                            <BaseTooltip
                                title={<TooltipContent>{tooltipText}</TooltipContent>}
                                icon={<TooltipIcon />}
                                arrow={false}
                                placement="right"
                            />
                        </FrameHeader>
                        <BodySmSmMedium>{subheader}</BodySmSmMedium>
                    </Frame>
                    {!isLocked && (
                        <PathwaysAvailable>
                            {!!isLearningDataAvailable && (
                                <>
                                    {totalLearningCards} {t`pathways available`}
                                </>
                            )}
                            <ArrowsWrapper>
                                {!!showCarouselControls && <>{renderArrows(prev, next, hasPrev, hasNext)}</>}
                            </ArrowsWrapper>
                        </PathwaysAvailable>
                    )}
                </LearningCarouselHeader>
                {!isLearningDataAvailable &&
                    (isLocked ? (
                        <LockedContentNoItemsWrapper>
                            <LockedContent elementIdToScrollTo={LEARNING_TAB_BAR_SECTION_ID} />
                        </LockedContentNoItemsWrapper>
                    ) : (
                        <EmptyResult
                            title={t`No learnings`}
                            description={t`Provided criteria doesnt match with your learnings`}
                        />
                    ))}
                {!!isLearningDataAvailable && (
                    <LearningCarouselItems>
                        {!!isLocked && <LockedContent elementIdToScrollTo={LEARNING_TAB_BAR_SECTION_ID} />}

                        <BaseCarousel
                            items={learningCardItems}
                            renderItem={(learning) => (
                                <LearningCard key={learning.id} learning={learning} onBookmarkClick={onBookmarkClick} />
                            )}
                            onInit={setEmblaApi}
                            onSelect={setIndex}
                            slideWidth={SLIDE_WIDTH}
                            options={{ slidesToScroll: slidesPerView }}
                            loop={isLocked}
                            grid
                            pageHeight={pageHeight}
                            gridItemsSize={gridItemsSize}
                            columnsPerRow={columnsPerRow}
                        />
                    </LearningCarouselItems>
                )}
            </LearningCarousels>
            {!!showCarouselControls && (
                <ShowMore $showMore={isShowingMore}>
                    {isShowingMore ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    <ShowMoreTextWrapper onClick={toggleShowMore}>
                        {isShowingMore ? t`Show less` : t`Show more`}
                    </ShowMoreTextWrapper>
                    {!!isShowingMore && <>{renderArrows(prev, next, hasPrev, hasNext)}</>}
                </ShowMore>
            )}
        </>
    );
};

export default MyLearningTab;
